import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import styles from './BottomSheet.module.scss';

interface BottomSheetProps {
    /** Controls visibility of the sheet */
    isOpen: boolean;
    /** Called when the user closes the sheet (overlay click or X button) */
    onClose: () => void;
    /** Sheet title */
    title?: string;
    /** Optional subtitle displayed above the title (e.g. "DISPOSAL TIPS") */
    subtitle?: string;
    /** Optional icon element rendered before the title */
    icon?: React.ReactNode;
    /** Main body content */
    children: React.ReactNode;
    /** Optional footer content (e.g. CTA button) */
    footer?: React.ReactNode;
    /** Accessible label for the dialog */
    ariaLabel?: string;
}

/**
 * Generic bottom-sheet (modal drawer) component.
 * Provides the overlay, slide-up animation, header, scrollable body, and footer.
 * Consumers pass domain-specific content via children and footer props.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon,
    children,
    footer,
    ariaLabel,
}) => {
    const { t } = useTranslation();

    return (
        <div
            className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel || title || ''}
        >
            <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                {(title || icon) && (
                    <div className={styles.header}>
                        {icon && (
                            <div className={styles.headerIcon}>
                                {icon}
                            </div>
                        )}
                        <div className={styles.titleGroup}>
                            {subtitle && (
                                <p className={styles.subtitle}>{subtitle}</p>
                            )}
                            {title && (
                                <h2 className={`${styles.title} ${!subtitle ? styles.titleOnly : ''}`}>
                                    {title}
                                </h2>
                            )}
                        </div>
                        <button
                            className={styles.closeBtn}
                            onClick={onClose}
                            aria-label={t('close') || 'Close'}
                        >
                            <X size={20} aria-hidden="true" />
                        </button>
                    </div>
                )}

                {/* Body */}
                <div className={styles.body}>
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className={styles.footer}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BottomSheet;
